/* eslint-disable react/jsx-pascal-case */
import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { collection, onSnapshot } from "firebase/firestore";
import { useFirestore } from "../../firebase/FirestoreContext";
import "../../styles/global.css";
import NavigationSimple from "../../elements/NavigationSimple";
import ListItem from "../../elements/ListItem";
import { EnvContext } from "../../context/EnvContext";
import config from "../../config";
import FeaturedCarousel_MobileV2 from "../../elements/FeaturedCarousel_MobileV2";
import FeaturedCarousel_DesktopV2 from "../../elements/FeaturedCarousel_DesktopV2";
import Header from "../../elements/Header";
import useWindowSize from "../../utils/useWindowSize";
import FAQ from "../../elements/FAQ";
import PageLayout from "../../elements/PageLayout";
import HorizontalScroll from "../../elements/HorizontalScroll";
import isProductAvailable from "../../functions/isProductAvailable";
import getListingsCollection from "../../utils/getListingsCollection";

const breakpoints = [755]
const mq = breakpoints.map(bp => `@media (min-width: ${bp}px)`)

const CategoryPage = () => {
  const { catName } = useParams(); // Get category name from the URL
  const db = useFirestore();
  const { width } = useWindowSize();
  const isMobile = width <= 768;

  //
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [products, setProducts] = useState([]);

  const environment = useContext(EnvContext);
  const collectionPath = environment === "staging" ? config.environment.staging.collectionPath : config.environment.production.collectionPath;

  useEffect(() => {
    const unsubscribe = onSnapshot(
      getListingsCollection(db),
      (snapshot) => {
        const normalizedCategory = catName.trim().toLowerCase();

        let filteredProducts = [];

        const categoryConfig = config.categories.find((category) => category.categoryName.toLowerCase() === normalizedCategory);

        if (!categoryConfig) {
          setProducts([]);
          return;
        }

        // Define allowed subcategories or vibes
        const allowedSubcategories = categoryConfig.subcategories || [];
        const allowedVibes = categoryConfig.vibes || [];

        filteredProducts = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((product) => {
            if (categoryConfig.filterType.includes("categories")) {
              return product.product_category === categoryConfig.category;
            }

            if (categoryConfig.filterType.includes("subcategories")) {
              return product.subcategories && product.subcategories.some((sub) => allowedSubcategories.includes(sub));
            }

            if (categoryConfig.filterType.includes("vibes")) {
              return product.vibes && product.vibes.some((vibe) => allowedVibes.includes(vibe));
            }

            if (categoryConfig.filterType.includes("tags")) {
              return product.tags && product.tags.some((tag) => categoryConfig.tags.includes(tag));
            }

            if (categoryConfig.filterType.includes("popular_product")) {
              return product.popular_product;
            }

            if (categoryConfig.filterType.includes("staff_pick")) {
              return product.staff_pick;
            }

            return false;
          });

        // Sort by availability first (pushing unavailable items to the end) and then by order_category_page.
        filteredProducts.sort((a, b) => {
          const aUnavailable = !isProductAvailable(a);
          const bUnavailable = !isProductAvailable(b);

          if (aUnavailable && !bUnavailable) return 1;
          if (!aUnavailable && bUnavailable) return -1;

          const orderA = a.order_category_page || 0;
          const orderB = b.order_category_page || 0;

          if (orderA === 0 && orderB !== 0) return 1;
          if (orderA !== 0 && orderB === 0) return -1;
          if (orderA !== 0 && orderB !== 0) return orderA - orderB;

          return 0;
        });

        setProducts(filteredProducts);
      },
      (error) => {
        console.error("Error fetching products:", error);
      }
    );

    return () => unsubscribe();
  }, [db, catName, collectionPath]);

  //
  const toggleSubcategory = (sub) => {
    setSelectedSubcategories((prev) => (prev.includes(sub) ? prev.filter((s) => s !== sub) : [...prev, sub]));
  };


  // gent category name from config. find category by categoryName, and than get title
  const category = config.categories.find((category) => category.categoryName === catName);
  const categoryTitle = category ? category.title : "Category";
  const filteredProducts = selectedSubcategories.length
    ? products.filter((product) => product.subcategories?.some((sub) => selectedSubcategories.includes(sub)))
    : products;

  return (
    <PageLayout analyticsView="shop" analyticsTitle="Shop Page" showNewsletterModal>
      <Header search={false} />
      <div css={styles.container}>
        <div css={styles.wrapper}>
          <div css={{ height: 35 }} />
          <NavigationSimple categoryTitle={categoryTitle} />
          <div css={{ height: 10 }} />
          {/** Header Image */}
          {isMobile && <FeaturedCarousel_MobileV2 featuredCollection={`category_${category?.categoryName}_1`} />}
          {!isMobile && <FeaturedCarousel_DesktopV2 featuredCollection={`category_${category?.categoryName}_1`} />}

          {/** Subcategories chips */}
          {category?.subcategories && category.subcategories.length > 0 && (
            <HorizontalScroll>
              <div
                css={{
                  display: "flex",
                  padding: "20px 10px 10px",
                  gap: 10,
                  [mq[0]]: {
                    gap: 15,
                    padding: "30px 10px 10px",

                  },
                }}
              >
                {category.subcategories.map((sub) => (
                  <div
                    key={sub}
                    onClick={() => toggleSubcategory(sub)}
                    css={{
                      padding: "5px 10px",
                      borderRadius: 20,
                      cursor: "pointer",
                      border: "1px solid black",
                      color: selectedSubcategories.includes(sub) ? "white" : "black",
                      background: selectedSubcategories.includes(sub) ? "black" : "transparent",
                      whiteSpace: "nowrap",
                      [mq[0]]: {
                        fontSize: "1.2rem",
                        padding: "5px 15px",
                      },
                    }}
                  >
                    {sub}
                  </div>
                ))}
              </div>
            </HorizontalScroll>
          )}

          {/** Content */}
          <div css={{ height: 20 }} />
          <div className="category-page" css={{ display: "flex", flex: 1, flexDirection: "column" }}>
            {/** */}

            {/** */}
            <div css={styles.productGrid}>
              {filteredProducts.length > 0 ? (
                filteredProducts.map((product) => <ListItem key={product.id} product={product} displayType={"horizontal"} />)
              ) : (
                <div css={styles.noProducts}>No products found in this category.</div>
              )}
            </div>
          </div>
          <div css={{ height: 100 }} />
          <FAQ />
        </div>
      </div>
    </PageLayout>
  );
};

const styles = {
  container: {
    maxWidth: "1140px",
    margin: "0 auto",
  },
  wrapper: {
    maxWidth: "100%"
  },
  productGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    padding: "0px 0px",
    flex: 1,
    alignContent: "start",
    [mq[0]]: {
      gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
    },
  },
  noProducts: {
    textAlign: "center",
    padding: "50px 0",
    fontSize: "18px",
    opacity: 0.7,
  },
  backButton: {
    height: "50px",
    width: "50px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    alignContent: "center",
    alignSelf: "center",
    margin: "5px 0px 0 15px",
    background: "rgba(100,100,100,.07)",
    cursor: "pointer",
    borderRadius: "50%",
    paddingTop: "5px",
  },
};

export default CategoryPage;
